import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import useSeo from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/site";
import { faqs } from "@/mocks/faqs";

const groups = ["全部", "平台说明", "内容原则", "使用帮助"];

export default function Faq() {
  const [activeGroup, setActiveGroup] = useState("全部");

  const visible = useMemo(
    () => (activeGroup === "全部" ? faqs : faqs.filter((item) => item.group === activeGroup)),
    [activeGroup],
  );

  useSeo({
    title: "常见问题_事件追踪说明与信息来源原则｜瓜田雷达",
    description:
      "关于网曝吃瓜事件追踪的常见疑问：更新频率如何确定、热度值怎么计算、状态标签分别代表什么、信息来源与纠错方式，以及如何用搜索和筛选快速找到你关注的话题档案。",
    keywords: "事件追踪说明,热度值含义,状态标签,信息来源,内容纠错,常见问题",
    path: "/faq",
    breadcrumbs: [
      { name: "首页", path: "/" },
      { name: "常见问题", path: "/faq" },
    ],
    jsonLd: {
      "@type": "FAQPage",
      url: `${SITE_URL}/faq`,
      inLanguage: "zh-CN",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
  });

  return (
    <>
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="px-4 py-8 md:px-8 md:py-14">
            <Breadcrumbs
              items={[
                { name: "首页", path: "/" },
                { name: "常见问题", path: "/faq" },
              ]}
            />
            <div className="mt-6 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <h1 className="font-heading text-2xl font-black leading-tight text-foreground-950 md:text-[36px]">
                  常见问题
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  这里集中回答关于追踪范围、更新机制、信息来源与使用方式的疑问。如果下面没有你想问的，可以直接来信，我们会补充到这一页。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-information-line text-primary-500" aria-hidden="true" />
                  一句话原则
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  只记录有出处的公开信息，只排列时间顺序，不替任何一方下结论，也不展示与事件无关的个人信息。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="px-4 py-10 md:px-8 md:py-14">
            <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
              <nav aria-label="问答分组" className="lg:sticky lg:top-28 lg:self-start">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-foreground-500">
                  按类别查看
                </p>
                <ul className="mt-4 space-y-2">
                  {groups.map((group) => {
                    const active = group === activeGroup;
                    const count =
                      group === "全部"
                        ? faqs.length
                        : faqs.filter((item) => item.group === group).length;
                    return (
                      <li key={group}>
                        <button
                          type="button"
                          onClick={() => setActiveGroup(group)}
                          aria-pressed={active}
                          className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                            active
                              ? "bg-primary-500 text-background-50"
                              : "text-foreground-700 hover:bg-background-100"
                          }`}
                        >
                          <span className="whitespace-nowrap">{group}</span>
                          <span className="text-xs opacity-70">{count}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  {activeGroup === "全部" ? "全部问答" : activeGroup}
                  <span className="ml-2 text-sm font-normal text-foreground-500">
                    {visible.length} 条
                  </span>
                </h2>
                <div className="mt-5">
                  <FaqAccordion items={visible} defaultOpenId={visible[0]?.id} />
                </div>

                <div className="mt-8 flex flex-col gap-4 rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="font-heading text-base font-bold text-secondary-900">
                      还有其它疑问？
                    </h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-secondary-800/80">
                      关于内容原则、信息来源与纠错流程的更多说明，整理在关于我们页面，也可以直接写信给我们。
                    </p>
                  </div>
                  <Link
                    to="/about"
                    className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                  >
                    了解内容原则
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}